import React from 'react';

import { tv } from '../../tv';

/**
 * Swaps one presentation for another while the enclosing interactive
 * container is pointed at: a lock that becomes a download arrow when the
 * card it sits in is hovered.
 *
 * It exists because call sites cannot write utilities, so the group-hover
 * mechanism needs a name to be reachable. The container marks itself as the
 * group (an interactive Card does), and this component listens; the variant
 * compiles to the same touch-safe React Aria selector the container's own
 * hover uses, so a tap never triggers the swap and touch users always see
 * the resting presentation. That is the intended degradation: on touch the
 * affordance is the container's own press feedback, and the swap is an
 * enhancement, never the only signal.
 *
 * The whole thing is hidden from assistive technology. A visual that
 * depends on where the pointer happens to be is decoration by definition;
 * anything a screen reader needs must be carried by the container's label
 * or its visible text.
 */

const hoverSwapStyles = tv({
  slots: {
    /* Contributes no box of its own: the children lay out as if they were
     * direct children of whatever holds this. */
    root: 'ck:contents',
    rest: 'ck:inline-flex ck:group-hover:hidden',
    hovered: 'ck:hidden ck:group-hover:inline-flex',
  },
});

export interface HoverSwapProps {
  /** What shows normally, and always on touch. */
  rest: React.ReactNode;
  /** What shows while the enclosing interactive container is hovered. */
  hovered: React.ReactNode;
}

export const HoverSwap = React.forwardRef<HTMLSpanElement, HoverSwapProps>(function HoverSwap(
  { rest, hovered },
  ref,
) {
  const { root, rest: restSlot, hovered: hoveredSlot } = hoverSwapStyles();
  return (
    <span ref={ref} className={root()} aria-hidden data-component="hover-swap">
      <span className={restSlot()}>{rest}</span>
      <span className={hoveredSlot()}>{hovered}</span>
    </span>
  );
});
